import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  collection,
  collectionData,
  doc,
  docData,
  Firestore,
  getDoc
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { getChannelPublicSettings } from '@yoimo/client-sdk/channels';
import { ChannelPublicSettings } from '@yoimo/interfaces';
import { firstValueFrom, from } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Consent, Group, Terms } from '../home/channels-view/channel-settings/channel-consent-tab/channel-consent-tab.component';
import { AuthGuard } from './userSession.service';

type LogsProps =
  | Partial<{
      channelId: string;
      requests: Consent[];
      groups: Group[];
      terms: Terms[];
    }>
  | (
      | {
          requests: {
            id: string;
          }[];
          terms?: undefined;
        }
      | {
          terms: {
            id: string;
          }[];
          requests?: undefined;
        }
    );

@Injectable({
  providedIn: 'root',
})
export class ConsentTermsService {
  constructor(
    private readonly firestore: Firestore,
    private readonly functions: Functions,
    private readonly userSession: AuthGuard,
    private readonly http: HttpClient
  ) {}

  getChannelSettingsWithFallback(channelId: string) {
    return getChannelPublicSettings(this.firestore, channelId).pipe(
      catchError((error) => 
        from(getDoc(doc(this.firestore, 'channels', channelId, 'settings', 'public'))).pipe(
          map(docSnapshot => {
            if (!docSnapshot.exists()) {
              return {} as ChannelPublicSettings;
            }
            return docSnapshot.data() as ChannelPublicSettings;
          })
        )
      )
    );
  }

  async getConsentTermsData(channelId: string) {
    try {
      return await firstValueFrom(this.getChannelSettingsWithFallback(channelId));
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  getConsentStats(channelId: string) {
    const docRef = doc(
      this.firestore,
      'channels',
      channelId,
      'stats',
      'consents'
    );
    return docData(docRef);
  }

  async updateConsentLogs(consents: LogsProps) {
    const callable = httpsCallable(this.functions, 'channel-writeconsent');
    return (await callable(consents)).data;
  }

  getConsentReport(
    url: string,
    body: {
      channelId: string;
      nextToken: null | string;
    }
  ) {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.userSession.userToken}`,
    });
    return this.http.post(url, body, { headers: headers });
  }

  getConsentVersions(channelId: string) {
    const logsCollectionRef = collection(
      this.firestore,
      'channels',
      channelId,
      'channelConsentLogs'
    );
    return collectionData(logsCollectionRef);
  }
}
